/**
 * Financial Needs Analysis (FNA) Calculation Functions
 * Based on comprehensive FNA report structure
 */

/**
 * Calculate survivor needs (life insurance) for a spouse
 * @param {Object} params
 * @param {number} params.annualIncome - Current annual income
 * @param {number} params.spouseIncome - Spouse's annual income
 * @param {number} params.totalDebts - Total debts to pay off
 * @param {number} params.mortgageBalance - Mortgage balance
 * @param {number} params.educationCost - Total education funding needed
 * @param {number} params.emergencyFundNeed - Emergency fund needed
 * @param {number} params.finalExpenses - Final expenses estimate
 * @param {number} params.yearsUntilYoungest18 - Years until youngest child turns 18
 * @param {number} params.survivorAge - Age of surviving spouse
 * @param {number} params.lifeExpectancy - Expected age at death (default 81)
 * @param {number} params.existingInsurance - Existing life insurance coverage
 * @param {number} params.availableAssets - Assets available to fund needs
 * @param {number} params.inflationRate - Annual inflation rate (default 0.03)
 * @param {number} params.returnRate - Return rate on investments (default 0.05)
 * @returns {Object} Survivor needs calculation
 */
export function calculateSurvivorNeeds({
  annualIncome,
  spouseIncome,
  totalDebts = 0,
  mortgageBalance = 0,
  educationCost = 0,
  emergencyFundNeed = 0,
  finalExpenses = 10000,
  yearsUntilYoungest18 = 0,
  survivorAge = 0,
  lifeExpectancy = 81,
  existingInsurance = 0,
  availableAssets = 0,
  inflationRate = 0.03,
  returnRate = 0.05,
  incomeReplacementPctWithChildren = 0.7,
  incomeReplacementPctAfterChildren = 0.6,
}) {
  const householdIncome = annualIncome + spouseIncome;
  const yearsWithChildren = Math.max(0, yearsUntilYoungest18);
  const yearsAfterChildren = Math.max(0, lifeExpectancy - survivorAge - yearsWithChildren);

  let incomeReplacement = 0;

  // NEEDS approach: the family needs householdIncome × replacementPct per year to maintain lifestyle.
  // The surviving spouse already earns spouseIncome, so the ANNUAL INCOME GAP that life insurance
  // must fund = max(0, householdNeed - spouseIncome).  Discounting this gap to a lump sum gives
  // the income-replacement component of the insurance need.

  // Phase 1 — while children are at home
  if (yearsWithChildren > 0) {
    const annualNeedWithKids = householdIncome * incomeReplacementPctWithChildren;
    const annualGapWithKids = Math.max(0, annualNeedWithKids - spouseIncome);
    incomeReplacement += calculatePVAnnuity(annualGapWithKids, yearsWithChildren, returnRate, inflationRate);
  }

  // Phase 2 — after children have left home
  if (yearsAfterChildren > 0) {
    const annualNeedAfterKids = householdIncome * incomeReplacementPctAfterChildren;
    const annualGapAfterKids = Math.max(0, annualNeedAfterKids - spouseIncome);
    const pvPhase2 = calculatePVAnnuity(annualGapAfterKids, yearsAfterChildren, returnRate, inflationRate);
    // Discount phase 2 annuity back to today (it starts after yearsWithChildren)
    const discountFactor = Math.pow(1 + returnRate, -yearsWithChildren);
    incomeReplacement += pvPhase2 * discountFactor;
  }

  const totalNeed = totalDebts + mortgageBalance + educationCost + emergencyFundNeed + finalExpenses + incomeReplacement;
  const remainingNeed = Math.max(0, totalNeed - existingInsurance - availableAssets);
  const progress = totalNeed > 0 ? ((existingInsurance + availableAssets) / totalNeed) * 100 : 0;

  return {
    totalNeed,
    existingInsurance,
    availableAssets,
    remainingNeed,
    progress: Math.min(100, Math.max(0, progress)),
    breakdown: {
      debts: totalDebts,
      mortgage: mortgageBalance,
      education: educationCost,
      emergencyFund: emergencyFundNeed,
      finalExpenses,
      incomeReplacement,
    },
  };
}

/**
 * Calculate disability income needs
 * @param {Object} params
 * @param {number} params.annualSalary - Current annual salary
 * @param {number} params.percentNeeded - Percent of salary needed (default 0.6)
 * @param {number} params.existingDisabilityIncome - Existing disability income benefits
 * @param {number} params.socialSecurityBenefit - Estimated Social Security disability benefit (optional)
 * @returns {Object} Disability needs calculation
 */
export function calculateDisabilityNeeds({
  annualSalary,
  percentNeeded = 0.6,
  existingDisabilityIncome = 0,
  socialSecurityBenefit = 0,
}) {
  const annualNeed = annualSalary * percentNeeded;
  const remainingNeed = Math.max(0, annualNeed - existingDisabilityIncome - socialSecurityBenefit);
  const monthlyNeed = annualNeed / 12;
  const monthlyRemaining = remainingNeed / 12;
  const progress = annualNeed > 0 ? ((existingDisabilityIncome + socialSecurityBenefit) / annualNeed) * 100 : 0;

  return {
    annualNeed,
    monthlyNeed,
    existingDisabilityIncome,
    socialSecurityBenefit,
    remainingNeed,
    monthlyRemaining,
    progress: Math.min(100, Math.max(0, progress)),
  };
}

/**
 * Calculate long-term care needs
 * @param {Object} params
 * @param {number} params.currentAge - Current age
 * @param {number} params.ltcAge - Age when LTC need begins (default 75)
 * @param {number} params.monthlyNeedToday - Monthly LTC need in today's dollars (default 6000)
 * @param {number} params.inflationRate - LTC inflation rate (default 0.03)
 * @param {number} params.durationYears - Duration of LTC need (default 3)
 * @param {number} params.existingLTCBenefit - Existing LTC policy monthly benefit
 * @param {number} params.returnRate - Return rate on savings (default 0.04)
 * @returns {Object} LTC needs calculation
 */
export function calculateLTCNeeds({
  currentAge,
  ltcAge = 75,
  monthlyNeedToday = 6000,
  inflationRate = 0.03,
  durationYears = 3,
  existingLTCBenefit = 0,
  returnRate = 0.04,
}) {
  const yearsUntilLTC = Math.max(0, ltcAge - currentAge);
  const monthlyNeedAtLTC = monthlyNeedToday * Math.pow(1 + inflationRate, yearsUntilLTC);
  const remainingMonthlyNeed = Math.max(0, monthlyNeedAtLTC - existingLTCBenefit);
  const totalLTCCost = remainingMonthlyNeed * 12 * durationYears;
  
  // Lump sum needed at age 75
  const lumpSumAtLTC = calculatePVAnnuity(remainingMonthlyNeed * 12, durationYears, returnRate, 0);
  
  // Monthly savings needed until LTC age
  const monthlySavingsNeeded = calculateMonthlySavingsNeeded(lumpSumAtLTC, yearsUntilLTC, returnRate);
  
  const progress = monthlyNeedAtLTC > 0 ? (existingLTCBenefit / monthlyNeedAtLTC) * 100 : 0;

  return {
    monthlyNeedAtLTC,
    remainingMonthlyNeed,
    totalLTCCost,
    lumpSumAtLTC,
    monthlySavingsNeeded,
    existingLTCBenefit,
    progress: Math.min(100, Math.max(0, progress)),
  };
}

/**
 * Calculate emergency fund needs
 * @param {Object} params
 * @param {number} params.monthlySalary - Monthly salary (or household)
 * @param {number} params.monthsCoverage - Months of coverage needed (default 6)
 * @param {number} params.currentEmergencyFund - Current emergency fund balance
 * @returns {Object} Emergency fund calculation
 */
export function calculateEmergencyFund({
  monthlySalary,
  monthlyExpenses = 0,
  monthsCoverage = 6,
  currentEmergencyFund = 0,
}) {
  // Best practice: emergency fund should cover monthly EXPENSES (what you actually spend),
  // not gross salary.  Fall back to salary only if expenses are not provided.
  const monthlyBase = monthlyExpenses > 0 ? monthlyExpenses : monthlySalary;
  const totalNeed = monthlyBase * monthsCoverage;
  const remainingNeed = Math.max(0, totalNeed - currentEmergencyFund);
  const progress = totalNeed > 0 ? (currentEmergencyFund / totalNeed) * 100 : 0;

  return {
    totalNeed,
    monthlyBase,
    currentEmergencyFund,
    remainingNeed,
    progress: Math.min(100, Math.max(0, progress)),
  };
}

/**
 * Calculate debt management metrics
 * @param {Object} params
 * @param {Array} params.debts - Array of debt objects {balance, monthlyPayment, interestRate}
 * @param {number} params.monthlyIncome - Monthly earned income
 * @returns {Object} Debt management calculation
 */
export function calculateDebtManagement({ debts = [], monthlyIncome }) {
  const totalDebt = debts.reduce((sum, d) => sum + (d.balance || 0), 0);
  const totalMonthlyPayments = debts.reduce((sum, d) => sum + (d.monthlyPayment || 0), 0);
  const debtToIncomeRatio = monthlyIncome > 0 ? (totalMonthlyPayments / monthlyIncome) * 100 : 0;
  
  // Calculate years until paid off for each debt
  const debtDetails = debts.map((debt) => {
    const { balance, monthlyPayment, interestRate } = debt;
    if (!balance || !monthlyPayment || monthlyPayment <= 0) {
      return { ...debt, yearsUntilPaidOff: null };
    }
    const monthlyRate = (interestRate || 0) / 12 / 100;
    if (monthlyRate === 0) {
      const months = balance / monthlyPayment;
      return { ...debt, yearsUntilPaidOff: months / 12 };
    }
    // Using amortization formula
    const months = -Math.log(1 - (balance * monthlyRate) / monthlyPayment) / Math.log(1 + monthlyRate);
    return { ...debt, yearsUntilPaidOff: months / 12 };
  });

  return {
    totalDebt,
    totalMonthlyPayments,
    debtToIncomeRatio,
    debtDetails,
    isHealthy: debtToIncomeRatio < 20,
  };
}

/**
 * Calculate retirement needs
 * @param {Object} params
 * @param {number} params.currentAge - Current age
 * @param {number} params.retirementAge - Retirement age (default 65)
 * @param {number} params.householdIncome - Current household income
 * @param {number} params.retirementIncomePercent - Percent of income needed in retirement (default 0.8)
 * @param {number} params.retirementYears - Years in retirement (default 18)
 * @param {number} params.currentRetirementAssets - Current retirement assets
 * @param {number} params.monthlyRetirementContributions - Monthly contributions to retirement
 * @param {number} params.retirementGrowthRate - Growth rate on retirement assets
 * @param {number} params.socialSecurityBenefit - Annual Social Security benefit
 * @param {number} params.inflationRate - Inflation rate (default 0.03)
 * @param {number} params.returnRate - Return rate for savings (default 0.05)
 * @returns {Object} Retirement needs calculation
 */
export function calculateRetirementNeeds({
  currentAge,
  retirementAge = 65,
  householdIncome,
  retirementIncomePercent = 0.8,
  retirementYears = 18,
  currentRetirementAssets = 0,
  monthlyRetirementContributions = 0,
  retirementGrowthRate = 0.08,
  socialSecurityBenefit = 0,
  inflationRate = 0.03,
  returnRate = 0.05,
}) {
  const yearsUntilRetirement = Math.max(0, retirementAge - currentAge);
  const annualRetirementNeed = householdIncome * retirementIncomePercent;
  
  // Project current assets to retirement
  const projectedAssets = futureValue(currentRetirementAssets, yearsUntilRetirement, retirementGrowthRate) +
    futureValueOfAnnuity(monthlyRetirementContributions * 12, yearsUntilRetirement, retirementGrowthRate);
  
  // Calculate lump sum needed at retirement (PV of annuity)
  const lumpSumNeeded = calculatePVAnnuity(annualRetirementNeed, retirementYears, returnRate, inflationRate);
  
  // Subtract Social Security (PV of annuity)
  const socialSecurityPV = socialSecurityBenefit > 0 
    ? calculatePVAnnuity(socialSecurityBenefit, retirementYears, returnRate, inflationRate)
    : 0;
  
  const shortfall = Math.max(0, lumpSumNeeded - projectedAssets - socialSecurityPV);
  
  // Monthly savings needed
  const monthlySavingsNeeded = shortfall > 0 && yearsUntilRetirement > 0
    ? calculateMonthlySavingsNeeded(shortfall, yearsUntilRetirement, returnRate)
    : 0;
  
  const progress = lumpSumNeeded > 0 ? ((projectedAssets + socialSecurityPV) / lumpSumNeeded) * 100 : 0;

  return {
    lumpSumNeeded,
    projectedAssets,
    socialSecurityPV,
    shortfall,
    monthlySavingsNeeded,
    progress: Math.min(100, Math.max(0, progress)),
  };
}

/**
 * Calculate education funding needs
 * @param {Object} params
 * @param {Array} params.children - Array of child objects {name, currentAge, startAge, annualCostToday, percentToFund, years}
 * @param {number} params.currentEducationSavings - Current education savings
 * @param {number} params.educationInflationRate - Education inflation rate (default 0.06)
 * @param {number} params.returnRate - Return rate on savings (default 0.05)
 * @returns {Object} Education needs calculation
 */
export function calculateEducationNeeds({
  children = [],
  currentEducationSavings = 0,
  educationInflationRate = 0.06,
  returnRate = 0.05,
}) {
  let totalLumpSumNeeded = 0;
  const childDetails = [];

  children.forEach((child) => {
    const { currentAge, startAge, annualCostToday, percentToFund = 1, years = 4 } = child;
    const yearsUntilStart = Math.max(0, startAge - currentAge);
    
    // Calculate first year cost (inflation adjusted)
    const firstYearCost = annualCostToday * Math.pow(1 + educationInflationRate, yearsUntilStart);
    
    // Calculate total cost (PV of growing annuity)
    let totalCost = 0;
    for (let i = 0; i < years; i++) {
      const yearCost = firstYearCost * Math.pow(1 + educationInflationRate, i);
      const pv = yearCost / Math.pow(1 + returnRate, yearsUntilStart + i);
      totalCost += pv;
    }
    
    const costToFund = totalCost * percentToFund;
    totalLumpSumNeeded += costToFund;
    
    childDetails.push({
      ...child,
      firstYearCost,
      totalCost,
      costToFund,
    });
  });

  const shortfall = Math.max(0, totalLumpSumNeeded - currentEducationSavings);

  // Monthly savings needed: each child's cost must be fully funded by the time THAT child
  // starts college, not when the last child graduates.  We compute per-child monthly savings
  // and sum them — this gives a more accurate monthly cash-flow number.
  let monthlySavingsNeeded = 0;
  if (shortfall > 0 && children.length > 0) {
    // Allocate shortfall proportionally across children by their costToFund
    const totalCost = childDetails.reduce((s, c) => s + (c.costToFund || 0), 0);
    childDetails.forEach((child) => {
      if (!child.costToFund || totalCost === 0) return;
      const childProportion = child.costToFund / totalCost;
      const childShortfall = shortfall * childProportion;
      const yearsToSave = Math.max(1, (child.startAge || 18) - (child.currentAge || 0));
      monthlySavingsNeeded += calculateMonthlySavingsNeeded(childShortfall, yearsToSave, returnRate);
    });
  }

  const progress = totalLumpSumNeeded > 0 ? (currentEducationSavings / totalLumpSumNeeded) * 100 : 0;

  return {
    totalLumpSumNeeded,
    currentEducationSavings,
    shortfall,
    monthlySavingsNeeded,
    childDetails,
    progress: Math.min(100, Math.max(0, progress)),
  };
}

/**
 * Calculate cash flow
 * @param {Object} params
 * @param {number} params.monthlySalary - Monthly salary
 * @param {number} params.otherIncome - Other monthly income
 * @param {number} params.taxRate - Effective tax rate (default 0.2)
 * @param {number} params.monthlyDebtPayments - Monthly debt payments
 * @param {number} params.monthlySavings - Monthly savings contributions
 * @param {number} params.lifeInsurancePremiums - Monthly life insurance premiums
 * @param {number} params.lifestyleExpenses - Monthly lifestyle expenses
 * @returns {Object} Cash flow calculation
 */
export function calculateCashFlow({
  monthlySalary,
  otherIncome = 0,
  taxRate = 0.2,
  monthlyDebtPayments = 0,
  monthlySavings = 0,
  lifeInsurancePremiums = 0,
  lifestyleExpenses = 0,
}) {
  const grossIncome = monthlySalary + otherIncome;
  const taxes = grossIncome * taxRate;
  const netIncome = grossIncome - taxes;
  const totalExpenses = monthlyDebtPayments + monthlySavings + lifeInsurancePremiums + lifestyleExpenses;
  const discretionaryIncome = netIncome - totalExpenses;

  return {
    grossIncome,
    taxes,
    netIncome,
    totalExpenses,
    discretionaryIncome,
    monthlySavings,
    breakdown: {
      debtPayments: monthlyDebtPayments,
      savings: monthlySavings,
      lifeInsurance: lifeInsurancePremiums,
      lifestyle: lifestyleExpenses,
    },
  };
}

// Helper functions

function calculatePVAnnuity(payment, years, rate, inflationRate = 0) {
  if (rate === inflationRate) {
    return payment * years;
  }
  const realRate = (rate - inflationRate) / (1 + inflationRate);
  if (realRate === 0) {
    return payment * years;
  }
  return payment * ((1 - Math.pow(1 + realRate, -years)) / realRate);
}

function calculateMonthlySavingsNeeded(futureValue, years, annualRate) {
  if (years === 0 || annualRate === 0) {
    return futureValue / (years * 12);
  }
  const monthlyRate = annualRate / 12;
  const months = years * 12;
  return (futureValue * monthlyRate) / (Math.pow(1 + monthlyRate, months) - 1);
}

function futureValue(presentValue, years, rate) {
  return presentValue * Math.pow(1 + rate, years);
}

function futureValueOfAnnuity(payment, years, rate) {
  if (rate === 0) {
    return payment * years;
  }
  return payment * ((Math.pow(1 + rate, years) - 1) / rate);
}
